import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Mail, User, FileText, Users, Eye, Edit } from 'lucide-react';

const EmailPreviewModal = ({
    isOpen,
    onClose,
    onSend,
    emailData,
    loading = false,
    templateName
}) => {
    const [formData, setFormData] = useState({
        from_email: '',
        from_name: '',
        to_emails: [],
        cc_emails: [],
        bcc_emails: [],
        subject: '',
        html_content: '',
        text_content: ''
    });
    const [ccInput, setCcInput] = useState('');
    const [bccInput, setBccInput] = useState('');
    const [toInput, setToInput] = useState('');
    const [showPreview, setShowPreview] = useState(true); // Show preview by default
    const contentEditableRef = useRef(null);
    const isUserEditingRef = useRef(false);

    useEffect(() => {
        if (emailData && isOpen) {
            setFormData({
                from_email: emailData.from_email || '',
                from_name: emailData.from_name || '',
                to_emails: emailData.to_emails || [],
                cc_emails: emailData.cc_emails || [],
                bcc_emails: emailData.bcc_emails || [],
                subject: emailData.subject || '',
                html_content: emailData.html_content || '',
                text_content: emailData.text_content || ''
            });
            setToInput(Array.isArray(emailData.to_emails) ? emailData.to_emails.join(', ') : emailData.to_emails || '');
            setCcInput(Array.isArray(emailData.cc_emails) ? emailData.cc_emails.join(', ') : '');
            setBccInput(Array.isArray(emailData.bcc_emails) ? emailData.bcc_emails.join(', ') : '');
            isUserEditingRef.current = false;
        }
    }, [emailData, isOpen]);

    // Initialize contenteditable content when switching to edit mode
    useEffect(() => {
        if (contentEditableRef.current && !showPreview) {
            // Only set content if it's empty or different, and user is not actively editing
            if (!isUserEditingRef.current) {
                const currentContent = contentEditableRef.current.innerHTML.trim();
                const newContent = formData.html_content || '';
                if (currentContent !== newContent) {
                    contentEditableRef.current.innerHTML = newContent;
                }
            }
        }
    }, [showPreview,formData.html_content]); // Only run when switching between preview/edit modes

    // Update content when emailData changes (initial load)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (contentEditableRef.current && !showPreview && formData?.html_content) {
            if (!isUserEditingRef.current) {
                contentEditableRef.current.innerHTML = formData.html_content;
            }
        }
    }, [formData?.html_content, showPreview]); // Only when emailData changes

    const parseEmailList = (input) => {
        return input.split(',')
            .map(email => email.trim())
            .filter(email => email.length > 0);
    };

    const handleSend = () => {
        const emailPayload = {
            ...formData,
            to_emails: parseEmailList(toInput),
            cc_emails: parseEmailList(ccInput),
            bcc_emails: parseEmailList(bccInput)
        };
        onSend(emailPayload);
    };

    // const addEmailToList = (input, setInput, field) => {
    //     const emails = parseEmailList(input);
    //     if (emails.length > 0) {
    //         setFormData(prev => ({
    //             ...prev,
    //             [field]: [...(prev[field] || []), ...emails]
    //         }));
    //         setInput('');
    //     }
    // };

    const removeEmail = (email, field) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].filter(e => e !== email)
        }));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 px-4 overflow-y-auto py-8">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <Mail className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">Email Preview & Edit</h2>
                            {templateName && (
                                <p className="text-xs text-gray-500">Template: {templateName}</p>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        disabled={loading}
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {/* From */}
                    <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                            <User className="w-4 h-4" />
                            From
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <input
                                type="text"
                                value={formData.from_name}
                                onChange={(e) => setFormData(prev => ({ ...prev, from_name: e.target.value }))}
                                placeholder="From Name"
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                disabled={loading}
                            />
                            <input
                                type="email"
                                value={formData.from_email}
                                onChange={(e) => setFormData(prev => ({ ...prev, from_email: e.target.value }))}
                                placeholder="from@example.com"
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                disabled={loading}
                            />
                        </div>
                    </div>

                    {/* To */}
                    <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                            <Mail className="w-4 h-4" />
                            To
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={toInput}
                                onChange={(e) => setToInput(e.target.value)}
                                onBlur={() => {
                                    const emails = parseEmailList(toInput);
                                    setFormData(prev => ({ ...prev, to_emails: emails }));
                                }}
                                placeholder="email1@example.com, email2@example.com"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                disabled={loading}
                            />
                        </div>
                        {formData.to_emails.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                                {formData.to_emails.map((email, idx) => (
                                    <span
                                        key={idx}
                                        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                                    >
                                        {email}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* CC */}
                    <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                            <Users className="w-4 h-4" />
                            CC (Optional)
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={ccInput}
                                onChange={(e) => setCcInput(e.target.value)}
                                onBlur={() => {
                                    const emails = parseEmailList(ccInput);
                                    setFormData(prev => ({ ...prev, cc_emails: emails }));
                                }}
                                placeholder="cc1@example.com, cc2@example.com"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                disabled={loading}
                            />
                        </div>
                        {formData.cc_emails.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                                {formData.cc_emails.map((email, idx) => (
                                    <span
                                        key={idx}
                                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs"
                                    >
                                        {email}
                                        <button
                                            onClick={() => removeEmail(email, 'cc_emails')}
                                            className="hover:text-red-600"
                                            disabled={loading}
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* BCC */}
                    <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                            <Users className="w-4 h-4" />
                            BCC (Optional)
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={bccInput}
                                onChange={(e) => setBccInput(e.target.value)}
                                onBlur={() => {
                                    const emails = parseEmailList(bccInput);
                                    setFormData(prev => ({ ...prev, bcc_emails: emails }));
                                }}
                                placeholder="bcc@example.com"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                disabled={loading}
                            />
                        </div>
                        {formData.bcc_emails.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                                {formData.bcc_emails.map((email, idx) => (
                                    <span
                                        key={idx}
                                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs"
                                    >
                                        {email}
                                        <button
                                            onClick={() => removeEmail(email, 'bcc_emails')}
                                            className="hover:text-red-600"
                                            disabled={loading}
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Subject */}
                    <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                            <FileText className="w-4 h-4" />
                            Subject
                        </label>
                        <input
                            type="text"
                            value={formData.subject}
                            onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                            placeholder="Email subject"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={loading}
                        />
                    </div>

                    {/* Message Body */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                <FileText className="w-4 h-4" />
                                Message Body
                            </label>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setShowPreview(true)}
                                    className={`px-3 py-1 text-xs rounded-lg transition-colors flex items-center gap-1 ${
                                        showPreview
                                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                    disabled={loading}
                                >
                                    <Eye className="w-3 h-3" />
                                    Preview
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowPreview(false)}
                                    className={`px-3 py-1 text-xs rounded-lg transition-colors flex items-center gap-1 ${
                                        !showPreview
                                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                    disabled={loading}
                                >
                                    <Edit className="w-3 h-3" />
                                    Edit
                                </button>
                            </div>
                        </div>
                        
                        {showPreview ? (
                            <div className="border border-gray-300 rounded-lg p-4 bg-white min-h-[300px] max-h-[500px] overflow-y-auto">
                                <div 
                                    dangerouslySetInnerHTML={{ __html: formData.html_content || '<p class="text-gray-400 italic">No content</p>' }}
                                    className="prose prose-sm max-w-none"
                                    style={{
                                        fontFamily: 'system-ui, -apple-system, sans-serif',
                                        lineHeight: '1.6',
                                        color: '#374151'
                                    }}
                                />
                            </div>
                        ) : (
                            <div className="border border-gray-300 rounded-lg overflow-hidden">
                                {/* Rich Text Toolbar */}
                                <div className="flex items-center gap-1 p-2 bg-gray-50 border-b border-gray-200 flex-wrap">
                                    <button
                                        type="button"
                                        onClick={() => document.execCommand('bold', false, null)}
                                        className="px-2 py-1 text-sm font-bold hover:bg-gray-200 rounded"
                                        title="Bold (Ctrl+B)"
                                    >
                                        B
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => document.execCommand('italic', false, null)}
                                        className="px-2 py-1 text-sm italic hover:bg-gray-200 rounded"
                                        title="Italic (Ctrl+I)"
                                    >
                                        I
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => document.execCommand('underline', false, null)}
                                        className="px-2 py-1 text-sm underline hover:bg-gray-200 rounded"
                                        title="Underline (Ctrl+U)"
                                    >
                                        U
                                    </button>
                                    <div className="w-px h-6 bg-gray-300 mx-1" />
                                    <button
                                        type="button"
                                        onClick={() => document.execCommand('formatBlock', false, 'h1')}
                                        className="px-2 py-1 text-xs hover:bg-gray-200 rounded"
                                        title="Heading 1"
                                    >
                                        H1
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => document.execCommand('formatBlock', false, 'h2')}
                                        className="px-2 py-1 text-xs hover:bg-gray-200 rounded"
                                        title="Heading 2"
                                    >
                                        H2
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => document.execCommand('formatBlock', false, 'p')}
                                        className="px-2 py-1 text-xs hover:bg-gray-200 rounded"
                                        title="Paragraph"
                                    >
                                        P
                                    </button>
                                    <div className="w-px h-6 bg-gray-300 mx-1" />
                                    <button
                                        type="button"
                                        onClick={() => document.execCommand('insertUnorderedList', false, null)}
                                        className="px-2 py-1 text-sm hover:bg-gray-200 rounded"
                                        title="Bullet List"
                                    >
                                        •
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => document.execCommand('insertOrderedList', false, null)}
                                        className="px-2 py-1 text-sm hover:bg-gray-200 rounded"
                                        title="Numbered List"
                                    >
                                        1.
                                    </button>
                                    <div className="w-px h-6 bg-gray-300 mx-1" />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const url = prompt('Enter URL:');
                                            if (url) {
                                                document.execCommand('createLink', false, url);
                                            }
                                        }}
                                        className="px-2 py-1 text-xs hover:bg-gray-200 rounded"
                                        title="Insert Link"
                                    >
                                        Link
                                    </button>
                                </div>
                                
                                {/* Contenteditable Div - Preserves HTML */}
                                <div
                                    ref={contentEditableRef}
                                    contentEditable={!loading}
                                    suppressContentEditableWarning={true}
                                    onInput={(e) => {
                                        isUserEditingRef.current = true;
                                        const html = e.target.innerHTML;
                                        setFormData(prev => ({ ...prev, html_content: html }));
                                        // Reset flag after a short delay to allow external updates
                                        setTimeout(() => {
                                            isUserEditingRef.current = false;
                                        }, 100);
                                    }}
                                    onBlur={() => {
                                        isUserEditingRef.current = false;
                                    }}
                                    className="min-h-[300px] max-h-[500px] overflow-y-auto p-4 focus:outline-none"
                                    style={{
                                        fontFamily: 'system-ui, -apple-system, sans-serif',
                                        fontSize: '14px',
                                        lineHeight: '1.6',
                                        color: '#374151'
                                    }}
                                />
                            </div>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                            {showPreview 
                                ? 'This is how the email will appear to recipients' 
                                : 'Edit the message content above. HTML formatting is handled automatically.'}
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSend}
                        disabled={loading || !formData.to_emails.length || !formData.subject || !formData.html_content}
                        className="px-6 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Sending...
                            </>
                        ) : (
                            <>
                                <Send className="w-4 h-4" />
                                Send Email
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmailPreviewModal;

